import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FileText, ChevronDown, ChevronUp, PenTool, Check, ShieldCheck, Upload as UploadIcon } from 'lucide-react';
import ComplianceFooter from './ComplianceFooter';
import SignatureCanvas from 'react-signature-canvas';

interface NDAStepProps {
  onNDASigned: () => void;
  ndaSigned: boolean;
  userCompany: string;
  userCompanyAddress?: string;
  userStreetNumber?: string;
  userZipCode?: string;
  userCity?: string;
  userCountry?: string;
}

const NDAStep: React.FC<NDAStepProps> = ({ onNDASigned, ndaSigned, userCompany, userCompanyAddress, userStreetNumber, userZipCode, userCity, userCountry }) => {
  const navigate = useNavigate();
  const [expandedSections, setExpandedSections] = useState<number[]>([]);
  const [agreements, setAgreements] = useState({
    nda: false,
    nonCircumvention: false
  });
  const [signature, setSignature] = useState('');
  const [uploadedSignature, setUploadedSignature] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const signatureRef = useRef<SignatureCanvas>(null);

  // Generate current date in the required format
  const getCurrentDate = () => {
    const now = new Date();
    const day = now.getDate();
    const month = now.toLocaleString('en-US', { month: 'long' });
    const year = now.getFullYear();
    
    // Add ordinal suffix to day
    const getOrdinalSuffix = (day: number) => {
      if (day > 3 && day < 21) return 'th';
      switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    };
    
    return `${day}${getOrdinalSuffix(day)} day of ${month} ${year}`;
  };

  const ndaTerms = [
    {
      id: 1,
      title: 'PURPOSE',
      content: `The Disclosing Party possesses confidential and proprietary information relating to the potential sale of a luxury resort and private lagoon in the Maldives ("Transaction"). The Receiving Party has expressed interest in assisting with the sale by identifying potential investors or buyers. The Third Partner (IFED) serves as the economic development partner and is authorized to introduce this opportunity to its network and exclusively selected long-term clients. This Agreement is intended to protect the confidentiality of such information and establish the roles and responsibilities of all three parties.`
    },
    {
      id: 2,
      title: 'CONFIDENTIAL INFORMATION',
      content: `Confidential Information includes, but is not limited to:
• Financial data, business plans, operational details, and ownership structures related to the Transaction.
• Any discussions, negotiations, or terms relating to the sale.
• Any other proprietary or sensitive information shared by the Disclosing Party.

Confidential Information does not include information that:
(a) is publicly available at the time of disclosure;
(b) becomes publicly available through no fault of the Receiving Party or Third Partner;
(c) is lawfully received from a third party without a confidentiality obligation; or
(d) is independently developed by the Receiving Party or Third Partner without reference to the Confidential Information.`
    },
    {
      id: 3,
      title: 'OBLIGATIONS OF THE RECEIVING PARTY (INVESTOR)',
      content: `The Receiving Party (investor) agrees to:
• Maintain strict confidentiality of the Confidential Information and treat it as proprietary and sensitive.
• Use the Confidential Information solely for the purpose of evaluating the investment opportunity.
• Not disclose, share, or distribute Confidential Information to any third party without prior written consent from the Disclosing Party.
• Take reasonable precautions to prevent unauthorized use or disclosure, including secure storage and transmission of information.
• Not use the Confidential Information for any purpose other than evaluating the investment opportunity.
• Not contact the Disclosing Party or other involved third parties directly without prior coordination through the Third Partner (IFED).
• Provide feedback and interest level to the Third Partner (IFED) regarding the investment opportunity.
• Maintain professional conduct and integrity throughout the evaluation process.
• Not engage in any activities that could harm the reputation or interests of the Disclosing Party.
• Agree that IFED receives a 5% commission on top of the sale price for successful facilitation, payable to the respective family office of IFED.`
    },
    {
      id: 4,
      title: 'OBLIGATIONS OF THE THIRD PARTNER (IFED)',
      content: `The Third Partner (IFED) agrees to:
• Maintain strict confidentiality of the Confidential Information and treat it as proprietary and sensitive.
• Use the Confidential Information solely for the purpose of facilitating the Transaction and introducing qualified investors or buyers.
• Introduce this opportunity exclusively to its network and selected long-term clients who meet the Disclosing Party's criteria.
• Conduct due diligence on potential investors or buyers before making introductions to ensure they are qualified and serious.
• Provide regular updates to the Disclosing Party regarding introductions made and progress of discussions.
• Not disclose, share, or distribute Confidential Information to any unauthorized third party without prior written consent.
• Take reasonable precautions to prevent unauthorized use or disclosure, including secure storage and transmission of information.
• Act as the intermediary between the Disclosing Party and potential investors, coordinating all communications and negotiations.
• Ensure that all potential investors or buyers introduced are informed of the confidential nature of the information shared.
• Maintain records of all introductions made and discussions held for audit purposes.
• Act in good faith and with professional integrity throughout the transaction process.
• Not engage in any activities that could harm the reputation or interests of the Disclosing Party.
• Facilitate the evaluation process by providing necessary information to potential investors while maintaining confidentiality.
• Coordinate feedback and interest levels between the Disclosing Party and potential investors.
• Agree that IFED receives a 5% commission on top of the sale price for successful facilitation, payable to the respective family office of IFED.`
    },
    {
      id: 5,
      title: 'OBLIGATIONS OF THE DISCLOSING PARTY (PROMINENT REALITY)',
      content: `The Disclosing Party (Prominent Reality) agrees to:
• Provide accurate and complete Confidential Information to the Third Partner (IFED) for the purpose of facilitating the Transaction.
• Maintain the confidentiality of the Receiving Party's evaluation process and feedback.
• Not disclose the identity of potential investors to unauthorized parties.
• Provide timely responses to inquiries from the Third Partner regarding the investment opportunity.
• Act in good faith throughout the transaction process.
• Maintain professional conduct and integrity in all dealings with the Third Partner and potential investors.
• Provide necessary documentation and information to facilitate the evaluation process.
• Respect the confidentiality of potential investors' identities and evaluation feedback.
• Coordinate all communications through the Third Partner (IFED) as the designated intermediary.
• Not engage in any activities that could harm the reputation or interests of the Third Partner or potential investors.
• Agree that IFED receives a 5% commission on top of the sale price for successful facilitation, payable to the respective family office of IFED.`
    },
    {
      id: 6,
      title: 'TERM & TERMINATION',
      content: `This Agreement shall remain in effect for two (2) years from the Effective Date. The Receiving Party's and Third Partner's confidentiality and non-circumvention obligations shall survive termination for a period of five (5) years.`
    },
    {
      id: 7,
      title: 'NO OBLIGATION TO PROCEED',
      content: `This Agreement does not obligate any party to enter into any transaction. The Disclosing Party reserves the right to terminate discussions at any time without liability.`
    },
    {
      id: 8,
      title: 'RETURN OR DESTRUCTION OF MATERIALS',
      content: `Upon termination of this Agreement or upon request by the Disclosing Party, the Receiving Party and Third Partner shall promptly return or destroy all Confidential Information.`
    },
    {
      id: 9,
      title: 'GOVERNING LAW & DISPUTE RESOLUTION',
      content: `This Agreement shall be governed by the laws of the Maldives. Any disputes shall be resolved through arbitration in the Maldives.`
    },
    {
      id: 10,
      title: 'MISCELLANEOUS',
      content: `• No modifications to this Agreement shall be valid unless agreed to in writing by all parties.
• If any provision is found unenforceable, the remaining provisions shall remain in full force.
• This Agreement constitutes the entire understanding between the parties with respect to the subject matter hereof.`
    }
  ];

  const nonCircumventionTerms = [
    {
      id: 1,
      title: 'Non-Circumvention Agreement',
      content: `The Receiving Party and Third Partner (IFED) agree not to circumvent the Disclosing Party in any way that would exclude the Disclosing Party from the Transaction.

PERMITTED ACTIVITIES:
Both parties are permitted to engage with investors and potential buyers for the purpose of facilitating the Transaction, provided that:

• The Disclosing Party is kept informed of all engagements
• No direct agreements are made without the Disclosing Party's prior written approval
• Any introductions made must be disclosed to the Disclosing Party, ensuring transparency in the process
• IFED is allowed to introduce this opportunity to its network and exclusively selected long-term clients

PROHIBITED ACTIVITIES:
The Receiving Party and Third Partner are strictly prohibited from:

• Directly communicating with the resort's owners, operators, or management without prior written approval from the Disclosing Party
• Bypassing the Disclosing Party in securing a deal
• Making any agreements or commitments without the Disclosing Party's involvement

CONSEQUENCES:
Any attempt to bypass the Disclosing Party in securing a deal will result in legal action and financial penalties.`
    },
    {
      id: 2,
      title: 'Direct Contact Prohibition',
      content: 'The receiving party and third partner (IFED) agree not to directly contact, solicit, or engage with any employees, customers, suppliers, or business partners of the disclosing party without prior written authorization. IFED must coordinate all introductions through the proper channels established by the disclosing party.'
    },
    {
      id: 3,
      title: 'Business Relationship Protection',
      content: 'All business relationships, partnerships, and opportunities introduced through this data room process shall be protected from circumvention for a period of 3 years. IFED is authorized to introduce this opportunity to its network and exclusively selected long-term clients, provided that all introductions are properly documented and reported to the disclosing party.'
    },

    {
      id: 5,
      title: 'Penalty Provisions',
      content: 'Violation of non-circumvention provisions may result in immediate termination of access and potential legal action for damages. IFED is entitled to a 5% commission on any transaction between the introduced parties, also in the case of circumvention. Additional penalties may include loss of economic development partner status and exclusion from future opportunities.'
    }
  ];

  const toggleSection = (sectionId: number) => {
    setExpandedSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const handleAgreementChange = (agreement: 'nda' | 'nonCircumvention') => {
    setAgreements(prev => ({
      ...prev,
      [agreement]: !prev[agreement]
    }));
  };

  const clearSignature = () => {
    signatureRef.current?.clear();
    setSignature('');
    setUploadedSignature(null);
  };

  const handleSignatureUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setUploadedSignature(e.target.files[0]);
      setSignature('uploaded');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!agreements.nda || !agreements.nonCircumvention || !signature) {
      return;
    }

    setIsLoading(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      onNDASigned();
      navigate('/compliance');
    } catch (error) {
      console.error('Error signing agreements:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (ndaSigned) {
    navigate('/compliance');
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="max-w-4xl mx-auto"
    >
      <div className="card">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
            <FileText className="w-8 h-8" color="#cba864" />
          </div>
          <h2 className="text-2xl font-bold text-neutral-900 mb-2">
            Review & Sign Confidentiality Agreements
          </h2>
          <p className="text-neutral-600">
            Access to this data room is conditioned upon your acceptance of our Non-Disclosure and Non-Circumvention Agreements, which ensure the protection of proprietary and strategic information.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* NDA Section */}
          <div className="border border-neutral-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center">
              <FileText className="w-5 h-5 mr-2" color="#cba864" />
              Confidentiality & Non-Disclosure Agreement
            </h3>
            
            {/* Full NDA Text */}
            <div className="mb-6 p-4 bg-neutral-50 rounded-lg text-sm text-neutral-700 leading-relaxed">
              <p className="mb-4">
                This Confidentiality & Non-Disclosure Agreement ("Agreement") is made and entered into as of the {getCurrentDate()} by and between:
              </p>
              <p className="mb-4">
                <strong>Prominent Reality Private Limited</strong>, a company registered in the Maldives, with its principal place of business at Maimagumatheege, Male' 20256 ("Disclosing Party");
              </p>
              <p className="mb-4">
                <strong>International Federation for Economic Development (IFED)</strong>, which is under management of AC Attractive Corporation Ag, and has its office at the Langholzstrasse 7, in 6333 Hünenberg See, in Switzerland, as the economic development partner ("Third Partner"); and
              </p>
              <p className="mb-4">
                <strong>{userCompany}</strong>
                {userCompanyAddress || userStreetNumber || userZipCode || userCity || userCountry ? 
                  `, ${[userCompanyAddress, userStreetNumber, userZipCode, userCity, userCountry].filter(Boolean).join(', ')}` 
                  : ''}, acting as an individual or authorized representative ("Receiving Party").
              </p>
              
              <div className="space-y-4">
                {ndaTerms.map((term) => (
                  <div key={term.id} className="border border-neutral-200 rounded-lg">
                    <button
                      type="button"
                      onClick={() => toggleSection(term.id)}
                      className="w-full px-4 py-3 text-left flex items-center justify-between hover:bg-neutral-50"
                    >
                      <span className="font-medium text-neutral-900">{term.title}</span>
                      {expandedSections.includes(term.id) ? (
                        <ChevronUp className="w-5 h-5 text-neutral-500" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-neutral-500" />
                      )}
                    </button>
                    {expandedSections.includes(term.id) && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="px-4 pb-3 text-sm text-neutral-600 whitespace-pre-line"
                      >
                        {term.content}
                      </motion.div>
                    )}
                  </div>
                ))}
              </div>
              
              <p className="mt-4 text-center font-medium">
                IN WITNESS WHEREOF, the parties hereto have executed this Agreement as of the Effective Date.
              </p>
            </div>

            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={agreements.nda}
                onChange={() => handleAgreementChange('nda')}
                className="mt-1 w-4 h-4 border-neutral-300 rounded focus:ring-2 focus:ring-[#cba864] focus:border-[#cba864] text-[#cba864] checked:bg-[#cba864] checked:border-[#cba864]"
                style={{ accentColor: '#cba864' }}
              />
              <span className="text-sm text-neutral-700">
                I have read and agree to the Confidentiality & Non-Disclosure Agreement terms and conditions
              </span>
            </label>
          </div>

          {/* Non-Circumvention Section */}
          <div className="border border-neutral-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center">
              <FileText className="w-5 h-5 mr-2" color="#cba864" />
              Non-Circumvention Agreement
            </h3>
            
            <div className="space-y-3 mb-4">
              {nonCircumventionTerms.map((term) => (
                <div key={term.id} className="border border-neutral-200 rounded-lg">
                  <button
                    type="button"
                    onClick={() => toggleSection(term.id + 10)}
                    className="w-full px-4 py-3 text-left flex items-center justify-between hover:bg-neutral-50"
                  >
                    <span className="font-medium text-neutral-900">{term.title}</span>
                    {expandedSections.includes(term.id + 10) ? (
                      <ChevronUp className="w-5 h-5 text-neutral-500" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-neutral-500" />
                    )}
                  </button>
                  {expandedSections.includes(term.id + 10) && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="px-4 pb-3 text-sm text-neutral-600 whitespace-pre-line"
                    >
                      {term.content}
                    </motion.div>
                  )}
                </div>
              ))}
            </div>

            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={agreements.nonCircumvention}
                onChange={() => handleAgreementChange('nonCircumvention')}
                className="mt-1 w-4 h-4 border-neutral-300 rounded focus:ring-2 focus:ring-[#cba864] focus:border-[#cba864] text-[#cba864] checked:bg-[#cba864] checked:border-[#cba864]"
                style={{ accentColor: '#cba864' }}
              />
              <span className="text-sm text-neutral-700">
                I have read and agree to the Non-Circumvention Agreement terms and conditions
              </span>
            </label>
          </div>

          {/* Digital Signature */}
          <div className="border border-neutral-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center">
              <PenTool className="w-5 h-5 mr-2" color="#cba864" />
              Digital Signature
            </h3>
            <div className="space-y-4">
              <div className="border border-neutral-300 rounded-lg p-4 bg-neutral-50">
                <SignatureCanvas
                  ref={signatureRef}
                  canvasProps={{
                    className: 'w-full h-32 border border-neutral-300 rounded bg-white'
                  }}
                  onEnd={() => setSignature('signed')}
                />
                <div className="flex items-center mt-2 space-x-3">
                  <label className="flex items-center space-x-2 cursor-pointer text-primary-600">
                    <UploadIcon className="w-5 h-5" color="#cba864" />
                    <span className="text-sm">Upload Signature Image</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleSignatureUpload}
                    />
                  </label>
                  {uploadedSignature && (
                    <span className="text-xs text-neutral-700">{uploadedSignature.name}</span>
                  )}
                </div>
                {uploadedSignature && (
                  <div className="mt-2">
                    <img
                      src={URL.createObjectURL(uploadedSignature)}
                      alt="Uploaded signature preview"
                      className="h-16 object-contain border border-neutral-200 rounded"
                    />
                  </div>
                )}
              </div>
              <div className="flex space-x-3 mt-2">
                <button
                  type="button"
                  onClick={clearSignature}
                  className="btn-secondary text-sm"
                >
                  Clear Signature
                </button>
              </div>
              <p className="text-xs text-neutral-500">
                Please sign above or upload your signature to confirm your agreement to all terms and conditions
              </p>
            </div>
          </div>

          <button
            type="submit"
            disabled={!agreements.nda || !agreements.nonCircumvention || !signature || isLoading}
            className="btn-primary w-full flex items-center justify-center"
          >
            {isLoading ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Processing Agreement...
              </div>
            ) : (
              <>
                <Check className="w-5 h-5 mr-2" color="#cba864" />
                Sign Agreements & Continue
              </>
            )}
          </button>
        </form>

        <ComplianceFooter />
      </div>
    </motion.div>
  );
};

export default NDAStep; 